import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { getLabels } from "store/actions/labelActions";
import { editLabel, addLabel } from "store/actions/labelActions";

const LabelModal = ({
  isOpen,
  toggle,
  editMode = false,
  activeLabel = null,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const inputRef = useRef(null);
  const [labelData, setLabelData] = useState({
    labelName: "",
    labelDescription: "",
    labelURL: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLabelData({
      ...labelData,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/jpg",
      "image/svg",
      "image/webp",
    ];

    if (e.target.files.length) {
      const file = e.target.files[0];
      const fileType = file.type;

      if (allowedTypes.includes(fileType)) {
        setLabelData((prev) => ({
          ...prev,
          labelURL: file,
        }));
      } else {
        toast.error(
          "Invalid file type. Please use a jpeg, png, gif, jpg, svg, or webp file."
        );
      }
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setLabelData((prev) => ({ ...prev, labelURL: null }));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleAddLabel = () => {
    const payload = {
      labelName: labelData?.labelName,
      labelDescription: labelData?.labelDescription,
      labelURL: labelData?.labelURL,
      restaurantID: user?.restaurantID,
    };
    setIsLoading(true);
    dispatch(
      addLabel(
        payload,
        () => {
          setTimeout(() => {
            setLabelData({
              labelName: "",
              labelDescription: "",
              labelURL: null,
            });
            toggle();
            setIsLoading(false);
            dispatch(getLabels(user?.restaurantID, "", 5, 0));
            toast.success("Label added successfully");
          }, 2000);
        },
        () => {
          setIsLoading(false);
        }
      )
    );
  };

  const handleEditLabel = () => {
    const updatedData = {
      labelName: labelData?.labelName,
      labelDescription: labelData?.labelDescription,
      labelURL: labelData?.labelURL,
    };
    setIsLoading(true);
    dispatch(
      editLabel(
        activeLabel?.id,
        updatedData,
        () => {
          setTimeout(() => {
            setIsLoading(false);
            toggle();
            setLabelData({
              labelName: "",
              labelDescription: "",
              labelURL: null,
            });
            dispatch(getLabels(user?.restaurantID, "", 5, 0));
            toast.success("Label updated successfully");
          }, 2000);
        },
        () => {
          setIsLoading(false);
        }
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editMode) {
      handleEditLabel();
    } else {
      handleAddLabel();
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editMode && activeLabel) {
        setLabelData({
          labelName: activeLabel.labelName || "",
          labelDescription: activeLabel.labelDescription || "",
          labelURL: activeLabel.labelURL || null,
        });
      } else {
        setLabelData({
          labelName: "",
          labelDescription: "",
          labelURL: null,
        });
      }
    }
  }, [isOpen, editMode, activeLabel]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle} className="align-items-center border-bottom">
        {editMode ? "Edit Label" : "Add Label"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="labelName">Label Name</Label>
            <Input
              id="labelName"
              type="text"
              name="labelName"
              required
              placeholder="Enter Label Name"
              value={labelData?.labelName}
              onChange={handleChange}
            />
          </FormGroup>
          <div className="d-flex flex-column justify-content-start align-items-start mb-3">
            <Label>Label Image</Label>
            <label
              htmlFor="upload-button"
              className="form-file-input position-relative d-flex align-items-center justify-content-center p-4 w-100 rounded cursor-pointer"
            >
              {labelData?.labelURL ? (
                <>
                  <img
                    src={
                      typeof labelData?.labelURL === "string"
                        ? labelData?.labelURL
                        : URL.createObjectURL(labelData?.labelURL)
                    }
                    alt="label-img"
                    className="form-input-img"
                  />
                  <Button
                    size="sm"
                    color="danger"
                    className="position-absolute top-2 right-2"
                    onClick={handleRemoveImage}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <span className="fa-stack fa-2x">
                  <i className="fas fa-circle fa-stack-2x" />
                  <i className="fas fa-store fa-stack-1x fa-inverse" />
                </span>
              )}
            </label>
            <input
              id="upload-button"
              type="file"
              ref={inputRef}
              className="d-none"
              onChange={handleImageChange}
            />
          </div>
          <FormGroup>
            <Label for="labelDescription">Description</Label>
            <Input
              id="labelDescription"
              type="textarea"
              name="labelDescription"
              resize="none"
              rows={5}
              placeholder="Enter Label Description"
              required
              value={labelData?.labelDescription}
              onChange={(e) =>
                setLabelData((prev) => ({
                  ...prev,
                  labelDescription: e.target.value,
                }))
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-top">
          <Button
            color="primary"
            type="submit"
            disabled={
              isLoading ||
              !labelData?.labelName ||
              !labelData?.labelDescription ||
              !labelData?.labelURL
            }
          >
            {isLoading && <Spinner size="sm" className="mr-2" />}
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default LabelModal;
